import { useVerification } from '../contexts/VerificationContext'
import { SPACE_POLICY } from '../data/standings'
import { evaluatePolicy } from '../utils/verification'
import type { PolicyDecision } from '../utils/verification'
import type { AccessPolicy } from '../types/verification'

export interface SpaceAccess {
  /** The configured policy governing this space, or null when it is open. */
  policy: AccessPolicy | null
  decision: PolicyDecision | null
  allowed: boolean
}

/**
 * Whether the signed-in person meets the verification requirement for a
 * community or group.
 *
 * The requirement is a configured policy record looked up by space id — never a
 * condition written into the component — so operations can change what a space
 * requires without a code change. A space with no policy is genuinely open, and
 * this returns that rather than inventing a default requirement.
 *
 * The result is presentation only. The membership service evaluates the same
 * policy authoritatively; nothing here is a permission.
 */
export function useSpaceAccess(spaceId: string): SpaceAccess {
  const { policies, trust, primary } = useVerification()
  const policyId = SPACE_POLICY[spaceId]
  const policy = policyId ? policies.find((entry) => entry.id === policyId) ?? null : null

  if (!policy) return { policy: null, decision: null, allowed: true }

  const decision = evaluatePolicy(policy, trust, primary?.status ?? null)
  return { policy, decision, allowed: decision.allowed }
}
